import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import {
  ClockIcon,
  WrenchScrewdriverIcon,
  CubeIcon,
  ShoppingCartIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
  MapPinIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon
} from '@heroicons/react/24/outline';

const DailyTimesheet = () => {
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const sigCanvas = useRef(null);
  
  const [step, setStep] = useState('job-select');
  const [selectedJob, setSelectedJob] = useState(null);
  const [assignedJobs, setAssignedJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('hours');
  
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    regularHours: '',
    overtimeHours: '',
    travelHours: '',
    equipmentUsed: [],
    consumablesUsed: [],
    purchases: [],
    notes: '',
    customerSignature: null,
    customerNameSigned: '',
    location: null
  });
  
  const [equipmentList, setEquipmentList] = useState([]);
  const [consumablesList, setConsumablesList] = useState([]);

  useEffect(() => {
    loadAssignedJobs();
    loadEquipmentList();
    loadConsumablesList();
  }, []);

  const loadAssignedJobs = async () => {
    try {
      const token = localStorage.getItem('token');
      const today = new Date().toISOString().split('T')[0];
      
      const response = await fetch(`${backendUrl}/api/timesheets/my-assigned-jobs?date=${today}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAssignedJobs(data.jobs || []);
      }
    } catch (error) {
      console.error('Failed to load assigned jobs:', error);
    }
  };

  const loadEquipmentList = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/timesheets/equipment-list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setEquipmentList(data.equipment || []);
      }
    } catch (error) {
      console.error('Failed to load equipment:', error);
    }
  };

  const loadConsumablesList = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/timesheets/consumables-list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setConsumablesList(data.consumables || []);
      }
    } catch (error) {
      console.error('Failed to load consumables:', error);
    }
  };

  const selectJob = (job) => {
    setSelectedJob(job);
    setStep('entry');
  };

  const submitTimesheet = async () => {
    setLoading(true);
    
    try {
      const signature = sigCanvas.current ? sigCanvas.current.toDataURL() : null;
      
      // Get location
      const location = await new Promise((resolve) => {
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => resolve({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              timestamp: new Date().toISOString()
            }),
            () => resolve(null)
          );
        } else {
          resolve(null);
        }
      });
      
      const token = localStorage.getItem('token');

      // Custom (not listed) consumables → send to dispatch for approval
      const customConsumables = formData.consumablesUsed.filter(c => c.custom && (c.consumable || '').trim());
      customConsumables.forEach((c) => {
        fetch(`${backendUrl}/api/consumables/suggest`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: c.consumable.trim() })
        }).catch(() => {});
      });

      const timesheetData = {
        work_order_number: selectedJob.work_order_number,
        customer_name: selectedJob.customer_name,
        job_site: selectedJob.job_site,
        project_id: selectedJob.project_id,
        date: formData.date,
        regular_hours: parseFloat(formData.regularHours) || 0,
        overtime_hours: parseFloat(formData.overtimeHours) || 0,
        travel_hours: parseFloat(formData.travelHours) || 0,
        equipment_used: formData.equipmentUsed.filter(e => e.equipment),
        consumables_used: formData.consumablesUsed.filter(c => c.consumable),
        purchases: formData.purchases.filter(p => p.item),
        notes: formData.notes,
        customer_signature: signature,
        customer_name_signed: formData.customerNameSigned,
        customer_signed_at: new Date().toISOString(),
        location: location
      };
      
      const response = await fetch(`${backendUrl}/api/timesheets/daily`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(timesheetData)
      });
      
      if (response.ok) {
        setStep('success');
      } else {
        alert('Failed to submit timesheet');
      }
    } catch (error) {
      console.error('Submit failed:', error);
      alert('Failed to submit timesheet');
    } finally {
      setLoading(false);
    }
  };

  // Job Selection Screen
  if (step === 'job-select') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-4 pb-24">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center mb-6">
            <button onClick={() => navigate(-1)} className="mr-4 p-2 hover:bg-white/50 rounded-lg">
              <ArrowLeftIcon className="h-6 w-6 text-gray-700" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Daily Timesheet</h1>
              <p className="text-sm text-gray-600">{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </div>
          </div>

          <div className="space-y-3">
            {assignedJobs.length === 0 ? (
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 text-center border border-blue-100 shadow-sm">
                <ClockIcon className="h-16 w-16 mx-auto mb-4 text-slate-700" />
                <p className="text-gray-600 font-medium">No jobs assigned for today</p>
                <p className="text-sm text-gray-500 mt-2">Contact dispatch for job assignment</p>
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-600 px-2">Select your job:</p>
                {assignedJobs.map((job, index) => (
                  <button
                    key={index}
                    onClick={() => selectJob(job)}
                    className="w-full bg-white/90 backdrop-blur-sm rounded-2xl p-5 border-2 border-blue-100 hover:border-blue-400 hover:shadow-lg transition-all text-left transform hover:scale-[1.02]"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="text-lg font-bold text-blue-600 mb-1">{job.work_order_number}</div>
                        <div className="text-gray-900 font-semibold mb-1">{job.customer_name}</div>
                        <div className="text-sm text-gray-600 flex items-center">
                          <MapPinIcon className="h-4 w-4 mr-1" />
                          {job.job_site}
                        </div>
                      </div>
                      <CheckCircleIcon className="h-6 w-6 text-slate-700" />
                    </div>
                  </button>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Entry Form Screen
  if (step === 'entry') {
    const addEquipment = () => {
      setFormData(prev => ({
        ...prev,
        equipmentUsed: [...prev.equipmentUsed, { equipment: '', quantity: 1, days: 1 }]
      }));
    };

    const updateEquipment = (index, field, value) => {
      setFormData(prev => ({
        ...prev,
        equipmentUsed: prev.equipmentUsed.map((item, i) => 
          i === index ? { ...item, [field]: value } : item
        )
      }));
    };

    const removeEquipment = (index) => {
      setFormData(prev => ({
        ...prev,
        equipmentUsed: prev.equipmentUsed.filter((_, i) => i !== index)
      }));
    };

    const addConsumable = () => {
      setFormData(prev => ({
        ...prev,
        consumablesUsed: [...prev.consumablesUsed, { consumable: '', quantity: 0 }]
      }));
    };

    const updateConsumable = (index, field, value) => {
      setFormData(prev => ({
        ...prev,
        consumablesUsed: prev.consumablesUsed.map((item, i) => 
          i === index ? { ...item, [field]: value } : item
        )
      }));
    };

    const removeConsumable = (index) => {
      setFormData(prev => ({
        ...prev,
        consumablesUsed: prev.consumablesUsed.filter((_, i) => i !== index)
      }));
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-4 pb-24">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="flex items-center mb-6">
            <button onClick={() => setStep('job-select')} className="mr-4 p-2 hover:bg-white/50 rounded-lg">
              <ArrowLeftIcon className="h-6 w-6 text-gray-700" />
            </button>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900">{selectedJob.work_order_number}</h1>
              <p className="text-sm text-gray-600">{selectedJob.customer_name}</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 bg-white/60 backdrop-blur-sm rounded-xl p-2">
            <button
              onClick={() => setActiveTab('hours')}
              className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
                activeTab === 'hours'
                  ? 'bg-blue-500 text-white shadow-lg'
                  : 'text-gray-700 hover:bg-white/70'
              }`}
            >
              <ClockIcon className="h-5 w-5 inline-block mr-2" />
              Hours
            </button>
            <button
              onClick={() => setActiveTab('equipment')}
              className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
                activeTab === 'equipment'
                  ? 'bg-blue-500 text-white shadow-lg'
                  : 'text-gray-700 hover:bg-white/70'
              }`}
            >
              <WrenchScrewdriverIcon className="h-5 w-5 inline-block mr-2" />
              Equipment
            </button>
            <button
              onClick={() => setActiveTab('consumables')}
              className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
                activeTab === 'consumables'
                  ? 'bg-blue-500 text-white shadow-lg'
                  : 'text-gray-700 hover:bg-white/70'
              }`}
            >
              <CubeIcon className="h-5 w-5 inline-block mr-2" />
              Consumables
            </button>
          </div>

          {/* Hours Tab */}
          {activeTab === 'hours' && (
            <div className="space-y-4">
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-blue-100 shadow-sm">
                <label className="block text-sm font-medium text-gray-700 mb-2">Regular Hours</label>
                <input
                  type="number"
                  step="0.5"
                  value={formData.regularHours}
                  onChange={(e) => setFormData(prev => ({ ...prev, regularHours: e.target.value }))}
                  className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-lg"
                  placeholder="8.0"
                />
              </div>

              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-blue-100 shadow-sm">
                <label className="block text-sm font-medium text-gray-700 mb-2">Overtime Hours</label>
                <input
                  type="number"
                  step="0.5"
                  value={formData.overtimeHours}
                  onChange={(e) => setFormData(prev => ({ ...prev, overtimeHours: e.target.value }))}
                  className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-lg"
                  placeholder="0.0"
                />
              </div>

              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-blue-100 shadow-sm">
                <label className="block text-sm font-medium text-gray-700 mb-2">Travel Hours</label>
                <input
                  type="number"
                  step="0.5"
                  value={formData.travelHours}
                  onChange={(e) => setFormData(prev => ({ ...prev, travelHours: e.target.value }))}
                  className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-lg"
                  placeholder="0.0"
                />
              </div>

              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-blue-100 shadow-sm">
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={4}
                  className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                  placeholder="Daily work notes..."
                />
              </div>
            </div>
          )}

          {/* Equipment Tab */}
          {activeTab === 'equipment' && (
            <div className="space-y-4">
              {formData.equipmentUsed.map((item, index) => (
                <div key={index} className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-blue-100 shadow-sm">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="font-semibold text-gray-800">Equipment {index + 1}</h3>
                    <button onClick={() => removeEquipment(index)} className="text-red-500 hover:text-red-700">
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Equipment Type</label>
                      <select
                        value={item.equipment}
                        onChange={(e) => updateEquipment(index, 'equipment', e.target.value)}
                        className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                      >
                        <option value="">Select equipment...</option>
                        {equipmentList.map((eq, i) => (
                          <option key={i} value={eq}>{eq}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Quantity</label>
                        <input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateEquipment(index, 'quantity', parseInt(e.target.value) || 1)}
                          className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Days</label>
                        <input
                          type="number"
                          min="1"
                          step="0.5"
                          value={item.days}
                          onChange={(e) => updateEquipment(index, 'days', parseFloat(e.target.value) || 1)}
                          className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              <button
                onClick={addEquipment}
                className="w-full py-4 bg-white/80 backdrop-blur-sm rounded-2xl border-2 border-dashed border-blue-300 hover:border-blue-500 hover:bg-blue-50 transition-all text-blue-600 font-medium flex items-center justify-center gap-2"
              >
                <PlusIcon className="h-5 w-5" />
                Add Equipment
              </button>
            </div>
          )}

          {/* Consumables Tab */}
          {activeTab === 'consumables' && (
            <div className="space-y-4">
              {formData.consumablesUsed.map((item, index) => (
                <div key={index} className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-blue-100 shadow-sm">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="font-semibold text-gray-800">Consumable {index + 1}</h3>
                    <button onClick={() => removeConsumable(index)} className="text-red-500 hover:text-red-700">
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Consumable Type</label>
                      {item.custom ? (
                        <div>
                          <input
                            type="text"
                            value={item.consumable}
                            onChange={(e) => updateConsumable(index, 'consumable', e.target.value)}
                            className="w-full px-4 py-3 bg-white border-2 border-amber-300 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-all"
                            placeholder='Type the consumable (e.g. 1/8" cutoff discs)'
                            data-testid={`custom-consumable-input-${index}`}
                          />
                          <p className="text-xs text-amber-600 mt-1.5">
                            New item — it goes to dispatch for approval and will be in the list for everyone once approved.
                          </p>
                          <button
                            onClick={() => { updateConsumable(index, 'custom', false); updateConsumable(index, 'consumable', ''); }}
                            className="text-xs text-blue-600 font-medium mt-1"
                            data-testid={`custom-consumable-back-${index}`}
                          >
                            ← back to the list
                          </button>
                        </div>
                      ) : (
                      <select
                        value={item.consumable}
                        onChange={(e) => {
                          if (e.target.value === '__custom__') {
                            updateConsumable(index, 'custom', true);
                            updateConsumable(index, 'consumable', '');
                          } else {
                            updateConsumable(index, 'consumable', e.target.value);
                          }
                        }}
                        className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                        data-testid={`consumable-select-${index}`}
                      >
                        <option value="">Select consumable...</option>
                        {consumablesList.map((cons, i) => (
                          <option key={i} value={cons}>{cons}</option>
                        ))}
                        <option value="__custom__">➕ Not listed — type it in</option>
                      </select>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Quantity</label>
                      <input
                        type="number"
                        step="0.1"
                        value={item.quantity}
                        onChange={(e) => updateConsumable(index, 'quantity', parseFloat(e.target.value) || 0)}
                        className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                        placeholder="0.0"
                      />
                    </div>
                  </div>
                </div>
              ))}

              <button
                onClick={addConsumable}
                className="w-full py-4 bg-white/80 backdrop-blur-sm rounded-2xl border-2 border-dashed border-blue-300 hover:border-blue-500 hover:bg-blue-50 transition-all text-blue-600 font-medium flex items-center justify-center gap-2"
              >
                <PlusIcon className="h-5 w-5" />
                Add Consumable
              </button>
            </div>
          )}

          {/* Continue to Review Button */}
          <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-white via-white to-transparent">
            <div className="max-w-3xl mx-auto">
              <button
                onClick={() => setStep('review')}
                className="w-full py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-[1.02]"
              >
                Continue to Review
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Review & Signature Screen
  if (step === 'review') {
    const totalHours = (parseFloat(formData.regularHours) || 0) + 
                       (parseFloat(formData.overtimeHours) || 0) + 
                       (parseFloat(formData.travelHours) || 0);

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-4 pb-24">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="flex items-center mb-6">
            <button onClick={() => setStep('entry')} className="mr-4 p-2 hover:bg-white/50 rounded-lg">
              <ArrowLeftIcon className="h-6 w-6 text-gray-700" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Review & Sign</h1>
          </div>

          {/* Summary Cards */}
          <div className="space-y-4 mb-6">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-blue-100 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-4">Job Details</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Work Order:</span>
                  <span className="font-semibold text-gray-900">{selectedJob.work_order_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Customer:</span>
                  <span className="font-semibold text-gray-900">{selectedJob.customer_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Location:</span>
                  <span className="font-semibold text-gray-900">{selectedJob.job_site}</span>
                </div>
              </div>
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-blue-100 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-4">Hours Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Regular Hours:</span>
                  <span className="font-semibold text-gray-900">{formData.regularHours || '0.0'} hrs</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Overtime Hours:</span>
                  <span className="font-semibold text-gray-900">{formData.overtimeHours || '0.0'} hrs</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Travel Hours:</span>
                  <span className="font-semibold text-gray-900">{formData.travelHours || '0.0'} hrs</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-gray-200">
                  <span className="font-semibold text-gray-900">Total Hours:</span>
                  <span className="font-bold text-blue-600 text-lg">{totalHours.toFixed(1)} hrs</span>
                </div>
              </div>
            </div>

            {formData.equipmentUsed.filter(e => e.equipment).length > 0 && (
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-blue-100 shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-4">Equipment Used</h3>
                <div className="space-y-2 text-sm">
                  {formData.equipmentUsed.filter(e => e.equipment).map((item, i) => (
                    <div key={i} className="flex justify-between">
                      <span className="text-gray-600">{item.equipment}</span>
                      <span className="font-semibold text-gray-900">Qty: {item.quantity} × {item.days} days</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {formData.consumablesUsed.filter(c => c.consumable).length > 0 && (
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-blue-100 shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-4">Consumables Used</h3>
                <div className="space-y-2 text-sm">
                  {formData.consumablesUsed.filter(c => c.consumable).map((item, i) => (
                    <div key={i} className="flex justify-between">
                      <span className="text-gray-600">{item.consumable}</span>
                      <span className="font-semibold text-gray-900">{item.quantity}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {formData.notes && (
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-blue-100 shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-2">Notes</h3>
                <p className="text-sm text-gray-700">{formData.notes}</p>
              </div>
            )}
          </div>

          {/* Customer Signature */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-blue-100 shadow-sm mb-6">
            <h3 className="font-semibold text-gray-900 mb-4">Customer Signature</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Customer Name</label>
              <input
                type="text"
                value={formData.customerNameSigned}
                onChange={(e) => setFormData(prev => ({ ...prev, customerNameSigned: e.target.value }))}
                className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                placeholder="Customer name..."
              />
            </div>

            <div className="border-2 border-gray-300 rounded-xl overflow-hidden bg-white">
              <SignatureCanvas
                ref={sigCanvas}
                canvasProps={{
                  className: 'w-full h-48',
                  style: { touchAction: 'none' }
                }}
              />
            </div>
            
            <button
              onClick={() => sigCanvas.current?.clear()}
              className="mt-3 w-full py-2 text-sm text-gray-600 hover:text-gray-900 font-medium"
            >
              Clear Signature
            </button>
          </div>

          {/* Submit Button */}
          <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-white via-white to-transparent">
            <div className="max-w-3xl mx-auto">
              <button
                onClick={submitTimesheet}
                disabled={loading || !formData.customerNameSigned || !totalHours}
                className="w-full py-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-2xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="h-6 w-6" />
                    Submit Timesheet
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success Screen
  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 flex items-center justify-center p-4">
        <div className="max-w-md mx-auto text-center">
          <div className="mb-6">
            <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto shadow-lg">
              <CheckCircleIcon className="h-16 w-16 text-white" />
            </div>
          </div>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Timesheet Submitted!</h1>
          <p className="text-gray-600 mb-8">
            Your timesheet for {selectedJob.work_order_number} has been submitted for approval.
          </p>
          
          <button
            onClick={() => {
              setStep('job-select');
              setSelectedJob(null);
              setFormData({
                date: new Date().toISOString().split('T')[0],
                regularHours: '',
                overtimeHours: '',
                travelHours: '',
                equipmentUsed: [],
                consumablesUsed: [],
                purchases: [],
                notes: '',
                customerSignature: null,
                customerNameSigned: '',
                location: null
              });
              loadAssignedJobs();
            }}
            className="px-8 py-3 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 transition-all"
          >
            Back to Jobs
          </button>
        </div>
      </div>
    );
  }
};

export default DailyTimesheet;
